import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import callApi, { abortableCallApi } from 'utils/callApi';
import { LOCATION_CHANGE } from 'react-router-redux';
import formatSinglePlural from 'utils/formatSinglePlural';

import { fetchAccountInformationSuccess } from 'redux/modules/account';
import { fetchAppInfoSuccess } from 'redux/modules/appInfo';
import { addNotification } from 'redux/modules/notifications';

// Actions
export const FETCH_NOTE_CATEGORIES = 'FETCH_NOTE_CATEGORIES';
export const FETCH_NOTE_CATEGORIES_SUCCESS = 'FETCH_NOTE_CATEGORIES_SUCCESS';
export const FETCH_NOTE_CATEGORIES_FAIL = 'FETCH_NOTE_CATEGORIES_FAIL';
export const CLEAR_NOTE_CATEGORIES = 'CLEAR_NOTE_CATEGORIES';
export const CREATE_NOTE_CATEGORY = 'CREATE_NOTE_CATEGORY';
export const CREATE_NOTE_CATEGORY_SUCCESS = 'CREATE_NOTE_CATEGORY_SUCCESS';
export const CREATE_NOTE_CATEGORY_FAIL = 'CREATE_NOTE_CATEGORY_FAIL';
export const UPDATE_NOTE_CATEGORY = 'UPDATE_NOTE_CATEGORY';
export const UPDATE_NOTE_CATEGORY_SUCCESS = 'UPDATE_NOTE_CATEGORY_SUCCESS';
export const UPDATE_NOTE_CATEGORY_FAIL = 'UPDATE_NOTE_CATEGORY_FAIL';
export const DELETE_NOTE_CATEGORY = 'DELETE_NOTE_CATEGORY';
export const DELETE_NOTE_CATEGORY_SUCCESS = 'DELETE_NOTE_CATEGORY_SUCCESS';
export const DELETE_NOTE_CATEGORY_FAIL = 'DELETE_NOTE_CATEGORY_FAIL';
export const REASSIGN_NOTE_CATEGORY = 'REASSIGN_NOTE_CATEGORY';
export const REASSIGN_NOTE_CATEGORY_SUCCESS = 'REASSIGN_NOTE_CATEGORY_SUCCESS';
export const REASSIGN_NOTE_CATEGORY_FAIL = 'REASSIGN_NOTE_CATEGORY_FAIL';

// Reducer
export default function noteCategoriesReducer(state = {
  data: [], count: 0, loading: 0,
}, action) {
  const { type } = action;
  let triggerLoading;
  let payload;
  if (action.payload) {
    ({ triggerLoading, ...payload } = action.payload);
  }
  switch (type) {
    case FETCH_NOTE_CATEGORIES:
      return {
        ...state,
        ...triggerLoading && state.loading <= 0 ? { loading: state.loading + 1 } : {},
      };
    case FETCH_NOTE_CATEGORIES_SUCCESS:
      return {
        ...state,
        ...payload,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case FETCH_NOTE_CATEGORIES_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case CLEAR_NOTE_CATEGORIES:
      return {
        ...state,
        data: [],
        count: 0,
      };
    case CREATE_NOTE_CATEGORY_SUCCESS: {
      const newData = [
        ...state.data.slice(0, state.data.length),
        payload,
      ];
      return {
        ...state,
        count: state.count + 1,
        data: newData,
      };
    }
    case UPDATE_NOTE_CATEGORY_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        if (dataItem.ID !== payload.ID) {
          return dataItem;
        }
        return {
          ...dataItem,
          ...payload,
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case DELETE_NOTE_CATEGORY_SUCCESS: {
      const index = state.data.findIndex(dataItem => dataItem.ID === payload.ID);
      const newData = [
        ...state.data.slice(0, index),
        ...state.data.slice(index + 1),
      ];
      return {
        ...state,
        count: state.count - 1,
        data: newData,
      };
    }
    case LOCATION_CHANGE:
      return {
        ...state,
        loading: 0,
      };
    default:
      return state;
  }
}

// Action Creators
export function fetchNoteCategories(payload) {
  return { type: FETCH_NOTE_CATEGORIES, payload };
}

export function fetchNoteCategoriesSuccess(payload) {
  return { type: FETCH_NOTE_CATEGORIES_SUCCESS, payload };
}

export function fetchNoteCategoriesFail(payload) {
  return { type: FETCH_NOTE_CATEGORIES_FAIL, payload };
}

export function clearNoteCategories(payload) {
  return { type: CLEAR_NOTE_CATEGORIES, payload };
}

export function createNoteCategory(payload) {
  return { type: CREATE_NOTE_CATEGORY, payload };
}

export function createNoteCategorySuccess(payload) {
  return { type: CREATE_NOTE_CATEGORY_SUCCESS, payload };
}

export function createNoteCategoryFail(payload) {
  return { type: CREATE_NOTE_CATEGORY_FAIL, payload };
}

export function updateNoteCategory(payload) {
  return { type: UPDATE_NOTE_CATEGORY, payload };
}

export function updateNoteCategorySuccess(payload) {
  return { type: UPDATE_NOTE_CATEGORY_SUCCESS, payload };
}

export function updateNoteCategoryFail(payload) {
  return { type: UPDATE_NOTE_CATEGORY_FAIL, payload };
}

export function deleteNoteCategory(payload) {
  return { type: DELETE_NOTE_CATEGORY, payload };
}

export function deleteNoteCategorySuccess(payload) {
  return { type: DELETE_NOTE_CATEGORY_SUCCESS, payload };
}

export function deleteNoteCategoryFail(payload) {
  return { type: DELETE_NOTE_CATEGORY_FAIL, payload };
}

export function reassignNoteCategory(payload) {
  return { type: REASSIGN_NOTE_CATEGORY, payload };
}

export function reassignNoteCategorySuccess(payload) {
  return { type: REASSIGN_NOTE_CATEGORY_SUCCESS, payload };
}

export function reassignNoteCategoryFail(payload) {
  return { type: REASSIGN_NOTE_CATEGORY_FAIL, payload };
}

// Requests

let abort;

export function requestFetchNoteCategories(payload) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = payload && payload.limit && payload.offset
    ? abortableCallApi(`resource/notes_category/?limit=${payload.limit}&offset=${payload.offset}`)
    : abortableCallApi('resource/notes_category/');
  abort = request.abort;
  return request.res;
}

export function requestSearchNoteCategories({ limit, offset, filters }) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = abortableCallApi('resource/notes_category/search/', 'POST', {
    filters: filters || [],
    sort_by: [
      {
        name: 'Name',
        sort: 'asc',
      },
      {
        name: 'ID',
        sort: 'asc',
      },
    ],
    limit,
    offset,
  });
  abort = request.abort;
  return request.res;
}

export function requestCreateNoteCategory(payload) {
  return callApi('resource/notes_category/', 'POST', payload);
}

export function requestUpdateNoteCategory(payload) {
  return callApi('resource/notes_category/', 'PATCH', payload);
}

export function requestDeleteNoteCategory(payload) {
  return callApi(`resource/notes_category/?id=${payload}`, 'DELETE');
}

export function requestReassignNoteCategory(payload) {
  return callApi('resource/notes_reassign/', 'POST', payload);
}

// Sagas
function* fetchNoteCategoriesSaga(action) {
  const { triggerLoading, filters, limit } = action.payload;
  try {
    const res = filters || limit
      ? yield call(requestSearchNoteCategories, action.payload)
      : yield call(requestFetchNoteCategories, action.payload);
    yield put(fetchNoteCategoriesSuccess({
      data: res.data,
      count: res.count,
      triggerLoading,
    }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(fetchNoteCategoriesFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* createNoteCategorySaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestCreateNoteCategory, values);
    yield put(createNoteCategorySuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'New Note Category successfully added',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(createNoteCategoryFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* updateNoteCategorySaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestUpdateNoteCategory, values);
    yield put(updateNoteCategorySuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Note Category successfully updated',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(updateNoteCategoryFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* deleteNoteCategorySaga(action) {
  const { values: { ID }, resolve, reject, showNotification = true } = action.payload;
  try {
    const res = yield call(requestDeleteNoteCategory, ID);
    yield put(deleteNoteCategorySuccess({ ID }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Note Category successfully deleted',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(deleteNoteCategoryFail(err.data));
    if (reject) {
      reject(err.data);
    }
    if (showNotification) {
      yield put(addNotification({
        message: 'Failed to delete Note Category',
        variant: 'warning',
      }));
    }
  }
}

function* reassignNoteCategorySaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestReassignNoteCategory, values);
    yield put(reassignNoteCategorySuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    // yield put(addNotification({
    //   message: res.data['updated order lines']
    //     ? `Workflow Status was successfully reassigned in ${res.data['updated order lines']} order ${formatSinglePlural({
    //           quantity: res.data['updated order lines'],
    //           single: 'line',
    //           plural: 'lines',
    //         })}`
    //     : 'Workflow Status was successfully reassigned',
    //   variant: 'success',
    // }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(reassignNoteCategoryFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

export function* noteCategoriesSaga() {
  yield takeLatest(FETCH_NOTE_CATEGORIES, fetchNoteCategoriesSaga);
  yield takeEvery(CREATE_NOTE_CATEGORY, createNoteCategorySaga);
  yield takeEvery(UPDATE_NOTE_CATEGORY, updateNoteCategorySaga);
  yield takeEvery(DELETE_NOTE_CATEGORY, deleteNoteCategorySaga);
  yield takeEvery(REASSIGN_NOTE_CATEGORY, reassignNoteCategorySaga);
}



// WEBPACK FOOTER //
// ./src/redux/modules/noteCategories.js